import { useState, useEffect } from "react";
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import { clsx, makeKey, styx } from "modules/Utils";
import CardSymphony from "elements/CardSymphony";
import { CSSProperties } from "react";
import PlatformLink, { PlatformItem } from "elements/PlatformLink";
import useCampaign from "Hooks/useCampaign";
import { ExternalLink } from "modules/Types";
import { Body2, Subtitle2 } from "components/shareable/Typography";
import InputSymphony from "elements/InputSymphony";
import ModalSymphony from "elements/ModalSymphony";
import ButtonSymphony from "elements/ButtonSymphony";

interface PlatformsCampaignProps {
    className: string;
    style?: CSSProperties;
}

const DEFAULT_PLATFORMS: PlatformItem[] = [
    {
        title: 'Spotify',
        description: 'Send fans to Spotify to listen to your music',
        name: 'spotify',
        icon: 'spotify',
        placeholder: 'Spotify URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'Apple Music',
        description: 'Send fans to Apple Music to listen to your music',
        name: 'appleMusic',
        icon: 'appleMusic',
        placeholder: 'Apple Music URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'YouTube Music',
        description: 'Send fans to YouTube Music to listen to your music',
        name: 'youtube',
        icon: 'youtube',
        placeholder: 'YouTube Music URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'TikTok',
        description: 'Send fans to TikTok to watch your videos',
        name: 'tiktok',
        icon: 'tiktok',
        placeholder: 'TikTok URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'Deezer',
        description: 'Send fans to Deezer to listen to your music',
        name: 'deezer',
        icon: 'deezer',
        placeholder: 'Deezer URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'Amazon Music',
        description: 'Send fans to Amazon Music to listen to your music',
        name: 'amazonMusic',
        icon: 'amazonMusic',
        placeholder: 'Amazon Music URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'Tidal',
        description: 'Send fans to Tidal to listen to your music',
        name: 'tidal',
        icon: 'tidal',
        placeholder: 'Tidal URL',
        link: '',
        isEnabled: false,
    },
    {
        title: 'Anghami',
        description: 'Send fans to Anghami to listen to your music',
        name: 'anghami',
        icon: 'anghami',
        placeholder: 'Anghami URL',
        link: '',
        isEnabled: false,
    },
]

/** Map external link names to platform names */
const PLATFORM_NAME_MAP: Record<string, string> = {
    'spotify': 'spotify',
    'apple_music': 'appleMusic',
    'youtube': 'youtube',
    'tiktok': 'tiktok',
    'deezer': 'deezer',
    'amazon_music': 'amazonMusic',
    'tidal': 'tidal',
    'anghami': 'anghami',
}

const PlatformsCampaign = ({
    className,
    style
}: PlatformsCampaignProps) => {
    const {
        enabledPlatforms,
        setEnabledPlatforms
    } = useCampaign()

    const [platforms, setPlatforms] = useState<PlatformItem[]>([])

    /** Custom Link Entry */
    const [customLinkUrlText, setCustomLinkUrlText] = useState<string>('')
    const [customLinkSelected, setCustomLinkSelected] = useState<PlatformItem | null>(null)
    const [customButtonText, setCustomButtonText] = useState<string>('')
    const [isNewLink, setIsNewLink] = useState<boolean>(false)
    const [invalidUrl, setInvalidUrl] = useState<boolean>(false)

    /** Track which platform is being edited */
    const [currentlyEditedPlatform, setCurrentlyEditedPlatform] = useState<PlatformItem | null>(null)

    /** Transform platforms data structure to include all possible platforms */
    const transformPlatformsData = (enabledPlatforms: ExternalLink[]): PlatformItem[] => {
        return DEFAULT_PLATFORMS.map(platform => {
            const existingPlatform = enabledPlatforms.find(
                link => PLATFORM_NAME_MAP[link.name] === platform.name
            )

            return {
                ...platform,
                link: existingPlatform?.url || '',
                isEnabled: !!existingPlatform
            }
        })
    }

    /** Initialize or update platforms when linksToPromote changes */
    useEffect(() => {
        if (!enabledPlatforms || enabledPlatforms.length === 0 || platforms.length > 0) return

        if (enabledPlatforms.length > 0 && platforms.length === 0) {
            const initialPlatforms = transformPlatformsData(enabledPlatforms)
            setPlatforms(initialPlatforms)
        }
    }, [enabledPlatforms])

    /** Handle platform enable/disable toggle */
    const handlePlatformToggle = (platformName: string, isEnabled: boolean) => {
        const updatedPlatforms = platforms.map(platform => {
            if (platform.name === platformName) {
                return { ...platform, isEnabled }
            }
            return platform
        })
        setPlatforms(updatedPlatforms)

        // Only include enabled platforms in enabledPlatforms
        const updatedEnabledPlatforms = updatedPlatforms
            .filter(platform => platform.isEnabled && platform.link)
            .map(platform => ({
                url: platform.link,
                name: Object.entries(PLATFORM_NAME_MAP)
                    .find(([_, value]) => value === platform.name)?.[0] || platform.name
            }))

        setEnabledPlatforms(updatedEnabledPlatforms)
    }

    const handleCloseEditLinkModal = () => {
        setCurrentlyEditedPlatform(null)
        setCustomLinkUrlText('')
        setCustomButtonText('')
        setInvalidUrl(false)
    }

    /** Handle platform link update */
    const handlePlatformLinkUpdate = (platformName: string, link: string) => {
        const updatedPlatforms = platforms.map(platform => {
            if (platform.name === platformName) {
                return { ...platform, link }
            }
            return platform
        })
        setPlatforms(updatedPlatforms)

        // Include all platforms that have links and are enabled
        const updatedEnabledPlatforms = updatedPlatforms
            .filter(platform => platform.isEnabled && platform.link)
            .map(platform => ({
                url: platform.link,
                name: Object.entries(PLATFORM_NAME_MAP)
                    .find(([_, value]) => value === platform.name)?.[0] || platform.name
            }))

        setEnabledPlatforms(updatedEnabledPlatforms)
    }

    /** Handles saving a new custom link */
    const saveNewLink = () => {
        if (!customLinkSelected || !customLinkUrlText) return

        // TODO: Implement save logic here

        // Reset form
        setCustomLinkUrlText('')
        setCustomButtonText('')
        setIsNewLink(false)
        setInvalidUrl(false)
    }

    /** Handle opening edit modal for a platform */
    const handleEditPlatform = (platform: PlatformItem) => {
        setCurrentlyEditedPlatform(platform)
        setCustomLinkUrlText(platform.link || '')
        setCustomButtonText(platform.title || '')
    }

    /** Handle saving edited platform link */
    const saveEditedLink = () => {
        if (!currentlyEditedPlatform || !customLinkUrlText.trim()) {
            setInvalidUrl(true)
            return
        }

        try {
            // Basic URL validation
            new URL(customLinkUrlText)
            setInvalidUrl(false)

            // Update the platform link
            handlePlatformLinkUpdate(currentlyEditedPlatform.name, customLinkUrlText.trim())

            // Enable the platform if it's not already enabled
            if (!currentlyEditedPlatform.isEnabled) {
                handlePlatformToggle(currentlyEditedPlatform.name, true)
            }

            // Reset form
            setCustomLinkUrlText('')
            setCustomButtonText('')
            setCurrentlyEditedPlatform(null)
        } catch (err) {
            setInvalidUrl(true)
        }
    }

    const renderLinkEntryModalContent = () => {
        return (
            <div
                className="renderLinkEntry"
                style={{ display: 'flex', gap: 24, flexDirection: 'column', marginBottom: 0 }}
            >
                <Body2 color={Colors.greyDark}>
                    Add information about your link.
                </Body2>
                <div
                    className="inputTitle"
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                    <Subtitle2>URL</Subtitle2>
                    <InputSymphony
                        className="customLinkUrlInput"
                        value={customLinkUrlText}
                        placeholder="Enter your link / URL"
                        onChange={(value) => {
                            setCustomLinkUrlText(value)
                            setInvalidUrl(false)
                        }}
                        error={invalidUrl ? 'Please enter a valid URL' : undefined}
                    />
                </div>
                {/* <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                    <Subtitle2 className="linkTypeTitle">Link Title</Subtitle2>

                    <InputSymphony
                        className="buttonTextInput"
                        value={customButtonText}
                        placeholder="Add button text"
                        onChange={(value) => setCustomButtonText(value)}
                    />
                </div> */}
            </div>
        )
    }

    const renderPlatformList = () => {
        return (
            <div className="platformsCampaignList" style={sty.platformsCampaignList}>
                {platforms.map((platform, index) => {
                    const {
                        title,
                        description,
                        icon,
                        link,
                        placeholder,
                        isEnabled,
                    } = platform

                    let linkEditText = link ? 'Edit Link' : 'Add Link'
                    return (
                        <div key={makeKey(platform.name, index)} style={sty.platformWrapper}>
                            <PlatformLink
                                className="platformsCampaignListItem"
                                title={title}
                                description={description}
                                icon={icon}
                                link={link}
                                placeholder={placeholder}
                                isEnabled={isEnabled}
                                onChange={(value) => handlePlatformLinkUpdate(platform.name, value)}
                                onToggleEnabled={(value) => handlePlatformToggle(platform.name, value)}
                            />
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderEditLinkModal = () => {
        return (
            <ModalSymphony
                buttonLeftText="Cancel"
                buttonRightText="Save"
                onClickButtonLeft={handleCloseEditLinkModal}
                onClickButtonRight={saveEditedLink}
                className="editLinkModal"
                title={`Edit ${currentlyEditedPlatform?.title || ''} Link`}
                isOpen={!!currentlyEditedPlatform}
                onClose={handleCloseEditLinkModal}
            > 
                {renderLinkEntryModalContent()}
            </ModalSymphony>
        )
    }

    return (
        <>
            <CardSymphony
                className={clsx('platformsCampaignComponent', className)}
                style={style}
                title="Streaming Platforms"
                subtitle="Select the streaming services you want to send fans to with your ads."
                size="small"
                isPadding16
            >
                {renderPlatformList()}
            </CardSymphony>

            {/* MODALS */}
            {renderEditLinkModal()}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    platformsCampaignList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
    },
    platformWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    editLink: {
        color: Colors.purple,
        cursor: 'pointer',
        marginLeft: 16
    }
}

export default PlatformsCampaign;